import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Check, Sparkles, Repeat2, ArrowRight } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo74RedisenoPatron() {
    const [patronActual, setPatronActual] = useState('');
    const [patronRediseñado, setPatronRediseñado] = useState('');
    const [regla, setRegla] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [viabilidad, setViabilidad] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/rediseno_patron`, { withCredentials: true });
                if (data.data) {
                    if (data.data.patronActual) setPatronActual(data.data.patronActual);
                    if (data.data.patronRediseñado) setPatronRediseñado(data.data.patronRediseñado);
                    if (data.data.regla) setRegla(data.data.regla);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'rediseno_patron', data: { patronActual, patronRediseñado, regla } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [patronActual, patronRediseñado, regla]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [patronActual, patronRediseñado, regla, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (patronActual.trim().length > 0 && patronRediseñado.trim().length > 0 && regla.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/rediseno-patron/submit`, {
                patron_actual: patronActual, "patron_rediseñado": patronRediseñado, regla
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setViabilidad(data.viabilidad);
            setSubmitted(true);
            toast.success('Patrón rediseñado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Repeat2 className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="rediseno-patron-success-title">Patrón rediseñado</h2>
                    <p className="text-[#5FAF9D] font-medium mb-4">Tu nuevo camino está definido</p>
                    {viabilidad && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Viabilidad: {viabilidad}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="rediseno_patron" chapter={7} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-rediseno-patron">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#7A9BA8] uppercase tracking-wide">Cap. 7</span><span className="text-xs text-[#7A8A93]">7.4</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Rediseño de patrón</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#5FAF9D] to-[#4A9A89] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><Repeat2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Reemplaza tu patrón</CardTitle><p className="text-xs sm:text-sm text-white/70">Compara el actual con uno más funcional</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="space-y-2 p-4 bg-[#E8B27A]/5 rounded-xl border border-[#E8B27A]/20">
                                <Label className="text-sm font-semibold text-[#E8B27A]">Patrón actual</Label>
                                <p className="text-xs text-[#7A8A93]">Lo que haces hoy cuando se activa el disparador</p>
                                <Textarea value={patronActual} onChange={e => setPatronActual(e.target.value)} placeholder="Ej: Correo del jefe → cierro todo → redes sociales → culpa → más retraso..." className="border-[#E8B27A]/20 bg-white rounded-xl min-h-[90px]" data-testid="input-patron-actual" />
                            </div>
                            <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                                <Label className="text-sm font-semibold text-[#5FAF9D]">Patrón rediseñado</Label>
                                <p className="text-xs text-[#7A8A93]">Lo que harás en su lugar</p>
                                <Textarea value={patronRediseñado} onChange={e => setPatronRediseñado(e.target.value)} placeholder="Ej: Correo del jefe → 3 respiraciones → respondo primera frase → sigo con mi plan..." className="border-[#5FAF9D]/20 bg-white rounded-xl min-h-[90px]" data-testid="input-patron-rediseñado" />
                            </div>
                        </div>
                        <div className="flex justify-center"><ArrowRight className="h-5 w-5 text-[#5FAF9D] rotate-90 lg:rotate-0" /></div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Regla de aplicación</Label>
                            <p className="text-xs text-[#7A8A93]">¿Cuándo y cómo activarás el nuevo patrón?</p>
                            <Textarea value={regla} onChange={e => setRegla(e.target.value)} placeholder='Ej: Cada vez que sienta la urgencia de cerrar el correo, aplicaré las 3 respiraciones y responderé solo la primera frase...' className="border-[#DCE3E8] rounded-xl min-h-[70px]" data-testid="input-regla-rediseno" />
                        </div>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-rediseno-patron">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Rediseñar patrón</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
